import * as React from 'react';
import { css } from '@emotion/css';
import { ReactiveComponent } from '@appbaseio/reactivesearch';
import { GenericObject } from '../../utils/types';
import { get } from 'lodash';
import createDOMPurify from 'dompurify';
import ResultsLayout from './ResultsLayout';
import {
  getDataFieldValue,
  getReactDependenciesFromPreferences,
  transformResultData,
} from '../../utils';
import { mediaMax } from '../../utils/media';
const DOMPurify = createDOMPurify(window);

const reactiveListCls = (toggleFilters: boolean, theme: object) => css`
  .custom-no-results {
    display: flex;
    justify-content: center;
    padding: 25px 0;
  }
  .custom-pagination {
    max-width: none;
    padding-bottom: 50px;
    a {
      border-radius: 2px;
    }
    a.active {
      color: ${get(theme, 'colors.textColor')};
    }
    @media (max-width: 768px) {
      display: ${toggleFilters ? 'none' : 'block'};
    }
  }
  .custom-powered-by {
    margin: 15px;
    display: none;
    visibility: hidden;
  }
  .custom-result-info {
    gap: 15px;
    padding: 18px 0px;
    height: 60px;
  }
  .custom-result-info > div {
    @media (max-width: 768px) {
      display: none;
    }
  }
  .custom-result-list {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    grid-gap: 10px;
    ${mediaMax.medium} {
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      display: ${toggleFilters ? 'none' : 'grid'};
    }
    ${mediaMax.small} {
      grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    }
  }
`;

export interface IResultsLayoutByCategoryProps {
  preferences?: GenericObject;
  isPreview: boolean;
  toggleFilters: boolean;
  componentProps: object;
  renderItem(data: object): void;
  render(hits: Array<any>): void;
}

export default function ResultsLayoutByCategory({
  preferences,
  isPreview = false,
  toggleFilters,
  componentProps,
  renderItem,
  render,
}: IResultsLayoutByCategoryProps) {
  const pageSettings = get(preferences, 'pageSettings', {});
  const componentSettings = get(
    pageSettings,
    `pages.${pageSettings.currentPage}.componentSettings`,
    {}
  );
  const resultSettings = get(
    componentSettings,
    'result',
    get(preferences, 'resultSettings', {})
  );
  const theme = get(preferences, 'themeSettings.rsConfig', {});
  return (
    <div>
      <ReactiveComponent
        preferencesPath={`pageSettings.pages.${pageSettings.currentPage}.componentSettings.result`}
        dataField={getDataFieldValue(
          get(resultSettings, 'fields.title', 'title')
        )}
        componentId="result"
        showLoader={false}
        defaultQuery={() => ({})}
        renderNoResults={() => (
          <div
            style={{ textAlign: 'right' }}
            // eslint-disable-next-line
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(
                get(
                  resultSettings,
                  'customMessages.noResults',
                  'No Results Found'
                )
              ),
            }}
          />
        )}
        renderResultStats={({
          numberOfResults,
          time,
        }: {
          numberOfResults: number;
          time: number;
        }) => (
          <div
            // eslint-disable-next-line
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(
                get(
                  resultSettings,
                  'customMessages.resultStats',
                  '[count] products found in [time] ms'
                )
                  .replace('[count]', numberOfResults)
                  .replace('[time]', time)
              ),
            }}
          />
        )}
        size={9}
        infiniteScroll
        render={({
          data,
          loading,
          triggerClickAnalytics,
        }: {
          data: any;
          loading: boolean;
          triggerClickAnalytics: any;
        }) => {
          const transformedData = transformResultData(
            data,
            resultSettings,
            isPreview
          );

          return (
            <div
              style={{
                position: 'relative',
              }}
            >
              {loading && (
                <div style={{ position: 'absolute', left: '50%' }}>
                  Loading Results...
                </div>
              )}
              {typeof renderItem === 'function' ||
              typeof render === 'function' ? (
                <>
                  {typeof renderItem === 'function' &&
                    transformedData.map(data => renderItem(data))}
                  {typeof render === 'function' && render(transformedData)}
                </>
              ) : (
                <ResultsLayout
                  data={transformedData}
                  preferences={preferences as GenericObject}
                  triggerClickAnalytics={triggerClickAnalytics}
                  isPreview={isPreview}
                  componentSettings={componentSettings}
                />
              )}
            </div>
          );
        }}
        innerClass={{
          list: 'custom-result-list',
          resultsInfo: 'custom-result-info',
          poweredBy: 'custom-powered-by',
          noResults: 'custom-no-results',
          pagination: 'custom-pagination',
        }}
        className={reactiveListCls(toggleFilters, theme)}
        react={{
          and: [
            'filter_by_product',
            ...getReactDependenciesFromPreferences(
              preferences as GenericObject,
              'result'
            ),
            'ToggleResults',
          ],
        }}
        {...componentProps}
      />
    </div>
  );
}
